import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  ICashFlow,
  ICashflowRentEntry,
  IPropertyDetail,
  IRent,
  IRentEntry,
  ITotalForDetails,
} from "../../../../interface/cashFlow";
import { formatCurrency, getCurrentFinancialYear } from "../../../../utils/commonUtil";
import { REIButton } from "../../../../components/ui";
import { Row } from "primereact/row";
import { ColumnGroup } from "primereact/columngroup";
import RentEditForm from "./EditRent";
import { useNavigate } from "react-router-dom";
import { useFilterSelection } from "../../FilterSelectionContext";

interface RentProps {
  cashFlowData: ICashFlow | undefined;
  year: string;
  isForecastOnly: boolean;
  isEditable: boolean;
  setIsEditable: (isEditable: boolean) => void;
}

const Rent: React.FC<RentProps> = ({ cashFlowData, year, isForecastOnly, isEditable, setIsEditable }) => {
  const [transformedData, setTransformedData] = useState<IRent>();
  const [collapsedRows, setCollapsedRows] = useState<Record<string, boolean>>({});
  const [transformedFooter, setTransformedFooter] = useState<IRent>();
  const currentYear = "20" + getCurrentFinancialYear();
  const { isShowingTotals } = useFilterSelection();
  const navigate = useNavigate();

  useEffect(() => {
    if (cashFlowData) {
      const transformed = transformRentData(cashFlowData.PropertyDetails, year);
      setTransformedData(transformed);
      const footer = TransformedFooter(cashFlowData.TotalForDetails, year);
      setTransformedFooter(footer);
    }
  }, [cashFlowData, year, isShowingTotals]);

  const transformRentData = (data: IPropertyDetail[], year: string) => {
    let groupCounter: number = -1;
    let lastGroupKey: string = "";
    return data.flatMap((property) => {
      const groupKey = property.PropertyDetail.StreetAddress;
      if (groupKey !== lastGroupKey) {
        groupCounter++;
        lastGroupKey = groupKey;
      }
      const actual: ICashflowRentEntry = isShowingTotals
        ? property.Details.CashflowRentTotal?.Actuals?.find((item) => item.Year === year) || {}
        : property.Details.CashflowRentPortion?.Actuals?.find((item) => item.Year === year) || {};
      const forecast: ICashflowRentEntry = isShowingTotals
        ? property.Details.CashflowRentTotal?.Forecast?.find((item) => item.Year === year) || {}
        : property.Details.CashflowRentPortion?.Forecast?.find((item) => item.Year === year) || {};
      const variance: ICashflowRentEntry = isShowingTotals
        ? property.Details.CashflowRentTotal?.Variance?.find((item) => item.Year === year) || {}
        : property.Details.CashflowRentPortion?.Variance?.find((item) => item.Year === year) || {};

      return [
        { type: "Forecast", data: forecast },
        { type: "Actuals", data: actual },
        { type: "Variance", data: variance },
      ].map(({ type, data }) => {
        const addressParts = [
          property.PropertyDetail.StreetAddress,
          property.PropertyDetail.Suburb,
          property.PropertyDetail.State,
          property.PropertyDetail.Postcode,
          property.PropertyDetail.Country,
        ]
          .filter((part) => part && part.trim() !== "")
          .join(", ");

        return {
          property: addressParts,
          type,
          annualRent: Number(data.RentPA) || 0,
          weeklyRent: Number(data.RentPW) || 0,
          leaseStart: data.LeaseStart || "",
          leaseEnd: data.LeaseEnd || "",
          nextRentReview: data.NextReview || "",
          tenants: data.Tenants || "",
          propertyId: property.Details.PortfolioTrackerId,
          groupIndex: groupCounter % 2,
        };
      });
    });
  };

  const TransformedFooter = (data: ITotalForDetails[], year: string): IRent => {
    const footerProperties = isShowingTotals
      ? data.find((item: any) => item.EntityId === "")?.CashflowDetailsTotals.Rent || ({} as IRent)
      : data.find((item: any) => item.EntityId === "")?.CashflowDetailsPortions.Rent || ({} as IRent);
    const actualFooter = footerProperties?.Actuals || ({} as IRentEntry[]);
    const forecastFooter = footerProperties?.Forecast || ({} as IRentEntry[]);
    const varianceFooter = footerProperties?.Variance || ({} as IRentEntry[]);

    return {
      Actuals: actualFooter,
      Forecast: forecastFooter,
      Variance: varianceFooter,
    };
  };

  const currencyTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value < 0 ? `(${formatCurrency(-value)})` : formatCurrency(value)}
    </span>
  );

  const toggleRowCollapse = (street: string) => {
    setCollapsedRows((prev) => ({ ...prev, [street]: !prev[street] }));
  };

  const renderFooter = (category: any, data) =>
    transformedFooter && (
      <Row>
        <Column footer={category == "Forecast" ? "Totals/averages" : ""} />
        <Column field="type" footer={category} />
        <Column footer={currencyTemplate(data?.RentPA || 0)} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.RentPW || 0)} footerStyle={{ textAlign: "right" }} />
        <Column footer={""} footerStyle={{ textAlign: "right" }} />
        <Column footer={""} footerStyle={{ textAlign: "right" }} />
        <Column footer={""} footerStyle={{ textAlign: "right" }} />
        <Column footer={""} footerStyle={{ textAlign: "right" }} />
      </Row>
    );

  const footerGroup =
    transformedFooter &&
    (Number(year) > Number(currentYear) ? (
      <ColumnGroup>
        {renderFooter("Forecast", transformedFooter.Forecast.find((x) => x.Year === year)!)}
        {!isForecastOnly && (
          <Row>
            <Column footer={""} />
            <Column footer={"Actual"} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
          </Row>
        )}
        {!isForecastOnly && (
          <Row>
            <Column footer={""} />
            <Column footer={"Variance"} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
          </Row>
        )}
      </ColumnGroup>
    ) : (
      <ColumnGroup>
        {renderFooter("Forecast", transformedFooter.Forecast.find((x) => x.Year === year)!)}
        {!isForecastOnly && renderFooter("Actuals", transformedFooter.Actuals.find((x) => x.Year === year)!)}
        {!isForecastOnly && renderFooter("Variance", transformedFooter.Variance.find((x) => x.Year === year)!)}
      </ColumnGroup>
    ));

  const onCancel = () => {
    isEditable = false;
    setIsEditable(false);
  };

  const renderCell = (rowData: any, field: string, templateFn: (value: number) => JSX.Element) => {
    const shouldRender = isForecastOnly
      ? collapsedRows[rowData.property] || rowData.type === "Forecast"
      : !collapsedRows[rowData.property] || rowData.type === "Forecast";

    if (
      Number(year) > Number(currentYear) &&
      rowData.type !== "Forecast" &&
      rowData[field] !== "Actuals" &&
      rowData[field] !== "Variance"
    ) {
      return null;
    }
    return shouldRender ? templateFn(rowData[field]) : null;
  };

  return (
    cashFlowData && (
      <div className="rent-container">
        {isEditable ? (
          <RentEditForm
            cashFlowData={cashFlowData}
            onCancel={onCancel}
            portfolioTrackerId={undefined}
            propertyName={undefined}
            setIsEditable={setIsEditable}
          />
        ) : (
          <DataTable
            value={transformedData}
            rowGroupMode="rowspan"
            groupRowsBy="property"
            footerColumnGroup={footerGroup}
            size="small"
            rowClassName={(rowData) => (rowData.groupIndex === 1 ? "groupRowGrey" : "")}
          >
            <Column
              field="property"
              header="Address/property name"
              body={(rowData) => (
                <div className="flex items-center">
                  {isForecastOnly && (
                    <REIButton
                      id={`mcfrent-btn-collapse-${rowData.propertyId}`}
                      icon={collapsedRows[rowData.property] ? "pi pi-angle-down" : "pi pi-angle-right"}
                      text={true}
                      onClick={() => toggleRowCollapse(rowData.property)}
                    />
                  )}
                  {!isForecastOnly && (
                    <REIButton
                      id={`mcfrent-btn-collapse-${rowData.propertyId}`}
                      icon={collapsedRows[rowData.property] ? "pi pi-angle-right" : "pi pi-angle-down"}
                      text={true}
                      onClick={() => toggleRowCollapse(rowData.property)}
                    />
                  )}
                  <a
                    id={`mcfrent-lnk-address-${rowData.propertyId}`}
                    data-testid={`mcfrent-lnk-address-${rowData.propertyId}`}
                    onClick={() => {
                      navigate(`/portfolio-tracker`, {
                        state: {
                          tab: "MyPropertyTab",
                          MyPropertyId: rowData.propertyId,
                        },
                      });
                    }}
                    className="cursor-pointer text-blue-700"
                  >{`${rowData.property}`}</a>
                </div>
              )}
            />
            <Column field="type" header="Type" body={(rowData) => renderCell(rowData, "type", (value) => value)} />
            <Column
              field="annualRent"
              header="Annual rent"
              style={{ textAlign: "right" }}
              align="right"
              body={(rowData) => renderCell(rowData, "annualRent", currencyTemplate)}
            />
            <Column
              field="weeklyRent"
              header="Weekly rent"
              align="right"
              style={{ textAlign: "right" }}
              body={(rowData) => renderCell(rowData, "weeklyRent", currencyTemplate)}
            />
            <Column
              field="leaseStart"
              header="Lease start"
              body={(rowData) => renderCell(rowData, "leaseStart", (value) => value)}
            />
            <Column
              field="leaseEnd"
              header="Lease end"
              body={(rowData) => renderCell(rowData, "leaseEnd", (value) => value)}
            />
            <Column
              field="nextRentReview"
              header="Next rent review"
              body={(rowData) => renderCell(rowData, "nextRentReview", (value) => value)}
            />
            <Column
              field="tenants"
              header="Tenants"
              body={(rowData) => renderCell(rowData, "tenants", (value) => value)}
            />
          </DataTable>
        )}
      </div>
    )
  );
};

export default Rent;
